import React, { useEffect, useState } from 'react';
import { 
  getAllClients, 
  createClient, 
  updateClient, 
  deleteClient 
} from '../utils/api';
import Header from '../components/Header';

interface Client {
  _id: string;
  clientCode: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
}

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    clientCode: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
  });
  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter((client) =>
      client.clientCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  const fetchClients = async () => {
    try {
      const data = await getAllClients();
      setClients(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await updateClient(editingId, formData);
        alert('Client updated successfully');
      } else {
        await createClient(formData);
        alert('Client created successfully');
      }
      setFormData({
        clientCode: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
      });
      setEditingId(null);
      setShowForm(false);
      fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save client');
    }
  };

  const handleEdit = (client: Client) => {
    setFormData({
      clientCode: client.clientCode,
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address,
      city: client.city,
      state: client.state,
    });
    setEditingId(client._id);
    setShowForm(true);
  };

  const handleDeleteClient = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await deleteClient(id);
        setClients(clients.filter((c) => c._id !== id));
        alert('Client deleted successfully');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete client');
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      clientCode: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
    });
  };

  if (loading) {
    return (
      <>
        <Header title="Client Management" />
        <div className="page-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Client Management" />
      <div className="page-container">
        <div className="page-header">
          <h1>Clients</h1>
          <button onClick={() => setShowForm(!showForm)} className="btn-primary">
            {showForm ? 'Cancel' : '+ Add New Client'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {showForm && (
          <div className="form-container">
            <h2>{editingId ? 'Edit Client' : 'Create New Client'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Client Code *</label>
                  <input
                    type="text"
                    name="clientCode"
                    value={formData.clientCode}
                    onChange={handleInputChange}
                    required
                    disabled={editingId !== null}
                    placeholder="e.g., C001"
                  />
                </div>

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Client name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="client@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="9876543210"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Street address"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="City"
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingId ? 'Update Client' : 'Create Client'}
                </button>
                <button type="button" onClick={handleCancel} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="search-section">
          <input
            type="text"
            placeholder="Search by code, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Client Code</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>City</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client._id}>
                  <td className="code-cell">{client.clientCode}</td>
                  <td>{client.name}</td>
                  <td>{client.email}</td>
                  <td>{client.phone}</td>
                  <td>{client.city}</td>
                  <td className="action-cell">
                    <button
                      onClick={() => handleEdit(client)}
                      className="btn-small btn-edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client._id)}
                      className="btn-small btn-delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredClients.length === 0 && (
            <p className="no-data">
              {searchTerm ? 'No clients found' : 'No clients yet. Create one to get started!'}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ClientManagement;
